/**
 * Per-shape stringifier for the review panel.
 *
 * The review screen is the confidence-building moment of the intake.
 * Earlier slices fell back to a literal `(structured value)` placeholder
 * for non-scalar answers, which reads as a leaked dev placeholder. This
 * module is the proper renderer: per-type formatters that produce
 * human-readable one-liners.
 *
 * Authoring rules:
 *
 *   - Address: NOT handled here. The catalog stores addresses as
 *     sibling fields (no `valueType: 'address'`), so address bags are
 *     composed by the review panel via `addressClustering.formatAddressBag`
 *     before reaching this stringifier (Phase 7-IA Slice 13).
 *   - Name: `"Jane M. Doe"` (middle-initial form) or `"Jane Doe"` if
 *     middle is empty. Suffix appended if present.
 *   - Date: ISO `YYYY-MM-DD` rendered as `"April 12, 1990"` via
 *     `toLocaleDateString`.
 *   - Document upload: filename if exposed via `IFilenameLookup`,
 *     otherwise `"Uploaded ✓"`.
 *   - Boolean: `"Yes"` / `"No"`.
 *   - Select / multi-select: chosen label(s) when a label resolver is
 *     provided (catalog-aware), otherwise the raw value(s).
 *   - Number / currency: number as a string, optionally prefixed `$`
 *     and grouped with thousands separators when `IFieldHints.currency`
 *     is true.
 *   - Anything genuinely unrecognized: `"Saved"` (one word, plain).
 *     Never leak `(structured value)` again.
 *
 * `stringifyAnswer` is intentionally pure: hooks like the filename
 * lookup are passed in via `IStringifyAnswerContext` rather than
 * imported, so tests can exercise every branch without mocking module
 * scope.
 */
import type { AnswerValue, FieldDefinition } from '@inside-us-aos/domain';

/**
 * Optional catalog hints. The function works without a field
 * definition (returns sensible scalar formatting), but a select/
 * multi-select field can render labels instead of raw enum keys when
 * the caller passes one.
 */
export interface IStringifyAnswerContext {
  /** Field definition — enables select/multi_select label resolution. */
  field?: FieldDefinition;
  /**
   * Maps an enum value to a human-readable label for `field`'s domain.
   * Called once per option; missing entries fall back to the raw value.
   */
  resolveOptionLabel?: (field: FieldDefinition, value: string) => string;
  /**
   * Maps an `uploadedFileId` to the original filename the user picked,
   * if known. Used by the document_upload renderer.
   */
  resolveFilename?: (uploadedFileId: string) => string | undefined;
  /**
   * Hints that drive number formatting. `currency: true` formats the
   * number with `$` and thousands separators.
   */
  hints?: {
    currency?: boolean;
  };
}

const FALLBACK = 'Saved';

/**
 * Formats an `AnswerValue` for inline display in the review panel.
 *
 * Returns:
 *   - `undefined` when the value is absent or empty (the caller
 *     suppresses the row in that case).
 *   - A non-empty string otherwise. Genuinely opaque values render
 *     as `"Saved"` rather than leaking a dev placeholder.
 */
export function stringifyAnswer(
  value: AnswerValue | undefined,
  context: IStringifyAnswerContext = {},
): string | undefined {
  if (value === undefined) return undefined;

  switch (value.type) {
    case 'text':
    case 'date': {
      const trimmed = value.value.trim();
      if (trimmed.length === 0) return undefined;
      if (value.type === 'date') return formatIsoDate(trimmed) ?? trimmed;
      return trimmed;
    }
    case 'select': {
      const trimmed = value.value.trim();
      if (trimmed.length === 0) return undefined;
      return resolveSelectLabel(trimmed, context);
    }
    case 'multi_select': {
      if (value.value.length === 0) return undefined;
      const labels = value.value.map(v => resolveSelectLabel(v, context));
      return labels.join(', ');
    }
    case 'boolean':
      return value.value ? 'Yes' : 'No';
    case 'number': {
      if (context.hints?.currency) return formatCurrency(value.value);
      return String(value.value);
    }
    case 'name':
      return formatName(value.value) ?? undefined;
    case 'document_upload': {
      const ids = value.value.uploadedFileIds;
      if (ids.length === 0) return undefined;
      if (context.resolveFilename) {
        for (const id of ids) {
          const filename = context.resolveFilename(id);
          if (filename && filename.length > 0) return filename;
        }
      }
      return 'Uploaded ✓';
    }
    default:
      return FALLBACK;
  }
}

function resolveSelectLabel(
  value: string,
  context: IStringifyAnswerContext,
): string {
  const { field, resolveOptionLabel } = context;
  if (field && resolveOptionLabel) {
    const label = resolveOptionLabel(field, value);
    if (label && label.length > 0) return label;
  }
  return value;
}

function formatName(name: {
  first: string;
  middle?: string;
  last: string;
  suffix?: string;
}): string | undefined {
  const first = (name.first ?? '').trim();
  const last = (name.last ?? '').trim();
  const middle = (name.middle ?? '').trim();
  const suffix = (name.suffix ?? '').trim();

  if (first.length === 0 && last.length === 0) return undefined;

  const parts: string[] = [];
  if (first.length > 0) parts.push(first);
  if (middle.length > 0) parts.push(`${middle.charAt(0).toUpperCase()}.`);
  if (last.length > 0) parts.push(last);
  let formatted = parts.join(' ');
  if (suffix.length > 0) formatted = `${formatted}, ${suffix}`;
  return formatted;
}

/**
 * Renders `YYYY-MM-DD` as `Month D, YYYY` (e.g., `April 12, 1990`).
 * Returns `undefined` if the input does not parse cleanly — the caller
 * falls back to the raw string in that case.
 */
function formatIsoDate(iso: string): string | undefined {
  // Strict ISO-date check: avoid `new Date('foo')` returning Invalid
  // Date for arbitrary strings. We only format when the catalog
  // contract is honored (`YYYY-MM-DD`).
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }
  // Construct in UTC to dodge timezone drift on the day boundary.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatCurrency(value: number): string {
  // Use Intl.NumberFormat for the thousands separator. Fixed to USD
  // because every currency-coded field in the catalog today is USD;
  // a future ADR can revisit when a non-USD field appears.
  //
  // Whole numbers render without trailing zeros (`$12,500`) while
  // values with a fractional component pin to two digits
  // (`$1,234.50`) — matching what an applicant expects to read on a
  // dollar amount.
  const hasFraction = !Number.isInteger(value);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(value);
}
